/**
 * Moving a patient to another ward or bed — DECISIONS.md #48.
 *
 * A move is the next version of the patient's encounter, so this form is a correction form in
 * shape: it starts from where the record says the patient is, names the exact version it supersedes,
 * and a move made from a screen that has gone out of date is refused rather than applied over somebody
 * else's. What it is not is a correction in meaning — the previous placement was true and stays true
 * for the time it covered, which is why the move carries its own time.
 *
 * **The move time defaults to now and can be set back** (#48): a move documented an hour after it
 * happened is the normal case (AGENTS.md §4). It is typed as `YYYY-MM-DD HH:mm` and read by the same
 * strict parser as an order's end date, never by the browser's date control (I18N.md §3).
 */

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { formatClinicalDateTime, parseClinicalDateTime } from '../format/clinicalDate'
import { useWardDirectory } from '../ward/wardDirectory'
import type { PatientLocationView } from './patientOverviewApi'
import { patientLocation } from './patientSearchDisplay'
import {
  transferPatient,
  TransferRefusedError,
  TransferUnavailableError,
  type TransferredPatient,
  type TransferRefusalCode,
  type TransferRefusalParams,
} from './transferApi'
import { transferRefusalMessage, transferSince, transferUnavailableMessage } from './transferDisplay'

/** Longest accepted bed label. Mirrors `PatientTransfer`'s limit on the server. */
const MAXIMUM_BED_LENGTH = 200

interface TransferDraft {
  readonly ward: string
  readonly bed: string
  /** `YYYY-MM-DD HH:mm` as typed, local time. */
  readonly movedAt: string
}

type Phase =
  | { readonly kind: 'editing' }
  | { readonly kind: 'saving' }
  | { readonly kind: 'moved'; readonly ward: string }

type Notice =
  | {
      readonly kind: 'refused'
      readonly code: TransferRefusalCode | 'UNKNOWN'
      readonly params: TransferRefusalParams
    }
  | { readonly kind: 'unavailable' }

/**
 * The typed move time as the UTC instant the server parses — or exactly as typed when it does not
 * name one local minute, for the server to refuse (`TRANSFER_MOVED_AT_MALFORMED`). Never repaired and
 * never dropped, for the reason `MedicationCorrectionForm`'s end date gives.
 */
function toInstant(typed: string): string {
  const parsed = parseClinicalDateTime(typed.trim())
  return parsed === null ? typed : parsed.toISOString()
}

/**
 * The transfer form.
 *
 * @param patientId Whose encounter is moved.
 * @param location Where the record says the patient is. Read on every render, so a refetch after a
 *   stale refusal moves the version this form supersedes along with it.
 * @param onMoved A new version was written; the overview refetches.
 * @param onStale Somebody else moved the patient. Refetches and keeps the draft.
 * @param onClose Leaves the form.
 */
export function PatientTransferForm({
  patientId,
  location,
  onMoved,
  onStale,
  onClose,
}: {
  readonly patientId: string
  readonly location: PatientLocationView
  readonly onMoved: (moved: TransferredPatient) => void
  readonly onStale: () => void
  readonly onClose: () => void
}) {
  const { t } = useTranslation(['common', 'patient', 'transfer'])

  const [phase, setPhase] = useState<Phase>({ kind: 'editing' })
  const [notice, setNotice] = useState<Notice | null>(null)
  const [confirmingDiscard, setConfirmingDiscard] = useState(false)

  // The time the form opened, as the default. Fixed at mount rather than recomputed on every render,
  // so the box does not change under a clinician who is reading it.
  const [openedAt] = useState(() => formatClinicalDateTime(new Date()))

  const [draft, setDraft] = useState<TransferDraft>(() => ({
    ward: location.ward ?? '',
    bed: location.bed ?? '',
    movedAt: openedAt,
  }))

  /** See {@link MedicationCorrectionForm}: a submit in flight is not aborted on unmount. */
  const live = useRef(true)
  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])

  const update = <Field extends keyof TransferDraft>(field: Field, value: TransferDraft[Field]) => {
    setDraft((previous) => ({ ...previous, [field]: value }))
    setNotice(null)
  }

  // The same one-request, never-refetched directory the ward list and registration use, because
  // every read of it is an audit row (AGENTS.md §4). Starts on the patient's current ward, so it
  // only picks one itself for a patient who has none.
  const wards = useWardDirectory(draft.ward === '' ? null : draft.ward, (ward) =>
    update('ward', ward),
  )

  const dirty =
    draft.ward !== (location.ward ?? '') ||
    draft.bed !== (location.bed ?? '') ||
    draft.movedAt !== openedAt
  const busy = phase.kind === 'saving'

  useEffect(() => {
    if (!dirty || phase.kind === 'moved') {
      return
    }

    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()

      // Both, for the reason `NoteEditor` gives.
      event.returnValue = 'unsaved'
    }

    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty, phase.kind])

  const submit = () => {
    if (busy) {
      return
    }

    setPhase({ kind: 'saving' })
    setNotice(null)

    const bed = draft.bed.trim()

    transferPatient(
      patientId,
      location.encounterId,
      {
        // Read here and not at mount: after a stale refusal and a refetch it is a different version.
        supersedes: location.encounterVersionId,
        ward: draft.ward,
        bed: bed === '' ? null : bed,
        movedAt: toInstant(draft.movedAt),
      },
      new AbortController().signal,
    ).then(
      (moved) => {
        if (!live.current) {
          return
        }
        setPhase({ kind: 'moved', ward: moved.ward })
        onMoved(moved)
      },
      (cause: unknown) => {
        if (!live.current) {
          return
        }

        setPhase({ kind: 'editing' })

        if (cause instanceof TransferRefusedError) {
          setNotice({ kind: 'refused', code: cause.code, params: cause.params })

          if (cause.code === 'TRANSFER_VERSION_STALE') {
            onStale()
          }
        } else if (cause instanceof TransferUnavailableError) {
          setNotice({ kind: 'unavailable' })
        } else {
          setNotice({ kind: 'refused', code: 'UNKNOWN', params: {} })
        }
      },
    )
  }

  const close = () => {
    if (dirty && phase.kind === 'editing') {
      setConfirmingDiscard(true)
      return
    }
    onClose()
  }

  if (phase.kind === 'moved') {
    return (
      <section className="registration patient-correction" aria-labelledby="transfer-form-heading">
        <h3 id="transfer-form-heading">{t('transfer:screen.heading')}</h3>

        <p className="registration__registered" role="status">
          {t('transfer:screen.saved', { ward: phase.ward })}
        </p>

        <button type="button" onClick={onClose}>
          {t('transfer:screen.close')}
        </button>
      </section>
    )
  }

  const since = transferSince(location.inPlaceSince, t)

  // Until the directory answers — or if it cannot — the current ward is the one option, so the box is
  // never empty and a bed move on the same ward still works.
  const current = draft.ward === '' ? [] : [{ ward: draft.ward, name: location.wardName ?? '' }]
  const offered = wards ?? current

  return (
    <section className="registration patient-correction" aria-labelledby="transfer-form-heading">
      <header className="registration__header">
        <h3 id="transfer-form-heading">{t('transfer:screen.heading')}</h3>

        <p className="registration__explains">{t('transfer:screen.explanation')}</p>

        {/* Where the patient is moving *from*, said before the fields: a move is relative to it, and
            it is also how a clinician notices they opened the wrong patient. */}
        <p className="registration__explains">
          {t('transfer:screen.from', {
            location: patientLocation({ ward: location.ward, bed: location.bed }, t),
            since,
          })}
        </p>
      </header>

      <p className="registration__hint">{t('transfer:screen.requiredHint')}</p>

      <div className="registration__field">
        <label htmlFor="transfer-field-ward">{t('transfer:field.ward')}</label>

        <p id="transfer-field-ward-hint" className="registration__hint">
          {t('transfer:fieldHint.ward')}
        </p>

        <select
          id="transfer-field-ward"
          value={draft.ward}
          disabled={busy}
          autoFocus
          aria-describedby="transfer-field-ward-hint"
          onChange={(event) => update('ward', event.target.value)}
        >
          {offered.map((ward) => (
            <option key={ward.ward} value={ward.ward}>
              {ward.name === '' ? ward.ward : `${ward.ward} — ${ward.name}`}
            </option>
          ))}
        </select>
      </div>

      <div className="registration__field">
        <label htmlFor="transfer-field-bed">
          {t('transfer:field.bed')}
          <span className="registration__optional"> ({t('transfer:screen.optional')})</span>
        </label>

        <p id="transfer-field-bed-hint" className="registration__hint">
          {t('transfer:fieldHint.bed')}
        </p>

        <input
          id="transfer-field-bed"
          type="text"
          value={draft.bed}
          disabled={busy}
          aria-describedby="transfer-field-bed-hint"
          maxLength={MAXIMUM_BED_LENGTH}
          onChange={(event) => update('bed', event.target.value)}
        />
      </div>

      <div className="registration__field registration__field--datetime">
        <label htmlFor="transfer-field-moved-at">{t('transfer:field.movedAt')}</label>

        <p id="transfer-field-moved-at-hint" className="registration__hint">
          {t('transfer:fieldHint.movedAt', { since })}
        </p>

        <input
          id="transfer-field-moved-at"
          // Text, not the browser's date control, and no maxLength — `MedicationCorrectionForm`'s end
          // date says why for both.
          type="text"
          value={draft.movedAt}
          disabled={busy}
          aria-describedby="transfer-field-moved-at-hint"
          onChange={(event) => update('movedAt', event.target.value)}
        />
      </div>

      <div aria-live="assertive">
        {notice !== null && (
          <div className="registration__notice" role="alert">
            <p>
              {notice.kind === 'unavailable'
                ? transferUnavailableMessage(t)
                : transferRefusalMessage(notice.code, notice.params, t)}
            </p>
          </div>
        )}
      </div>

      <footer className="registration__actions">
        <p className="registration__final">{t('transfer:screen.final')}</p>

        <button type="button" className="registration__register" disabled={busy} onClick={submit}>
          {busy ? t('transfer:screen.saving') : t('transfer:screen.save')}
        </button>

        <button type="button" disabled={busy} onClick={close}>
          {t('transfer:screen.cancel')}
        </button>
      </footer>

      {confirmingDiscard && (
        <div
          className="registration__discard"
          role="alertdialog"
          aria-label={t('transfer:screen.discardQuestion')}
        >
          <p>{t('transfer:screen.discardQuestion')}</p>

          <button type="button" onClick={() => setConfirmingDiscard(false)}>
            {t('transfer:screen.discardCancel')}
          </button>
          <button type="button" onClick={onClose}>
            {t('transfer:screen.discardConfirm')}
          </button>
        </div>
      )}
    </section>
  )
}
